/**
 * PublicLanding — root page for ELR Industries
 * Visible to all visitors without authentication.
 * Staff click "Staff Login" → /login → post-auth → /app
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Factory, Truck, FlaskConical, Layers, ChevronRight,
  Phone, Mail, MapPin,
} from 'lucide-react';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Products', href: '#services' },
  { label: 'Quality', href: '#quality' },
  { label: 'Contact', href: '#contact' },
];

const SERVICES = [
  {
    icon: Layers,
    title: 'Ready-Mix Concrete',
    desc: 'M15 to M40 grade concrete, batched to IS 4926 and delivered to your site by transit mixer.',
  },
  {
    icon: Truck,
    title: 'Pump & Delivery',
    desc: 'Boom pump service for high-rise and hard-to-access pours, backed by a GPS-tracked transit mixer fleet.',
  },
  {
    icon: FlaskConical,
    title: 'Quality & Lab Testing',
    desc: 'In-house lab: cube testing, sieve analysis, and mix design certified to IS 383:2016 on every batch.',
  },
  {
    icon: Factory,
    title: 'Bulk & Standing Supply',
    desc: 'Committed-volume supply agreements for large construction and infrastructure projects.',
  },
];

const QUALITY_POINTS = [
  { title: 'Automated Batching Plant', desc: 'Automated, calibrated batching for consistent yield and mix accuracy on every load.' },
  { title: 'IS-Code Lab Testing', desc: 'Cube strength, sieve analysis, flakiness index, and specific gravity tested in-house, not outsourced.' },
  { title: 'Full Batch Traceability', desc: 'Every delivery traces back to its grade, batch, and raw-material source.' },
];

export default function PublicLanding() {
  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans">
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#1F7A6E] shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/assets/elr-logo-teal.png"
              alt="ELR Industries"
              className="h-9 w-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>

          {/* Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href}
                className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                {l.label}
              </a>
            ))}
          </nav>

          {/* CTAs */}
          <div className="flex items-center gap-3">
            <a href="#contact"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white border border-white/30 rounded-sm hover:bg-white/10 transition-colors">
              Request a Quote
            </a>
            <Link to="/portal/login"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white border border-white/30 rounded-sm hover:bg-white/10 transition-colors"
              data-testid="customer-login-btn">
              Customer Login
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-white text-[#1F7A6E] rounded-sm hover:bg-slate-100 transition-colors"
              data-testid="staff-login-btn">
              Staff Login
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative bg-[#1F7A6E] overflow-hidden">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-xs font-semibold uppercase tracking-wider mb-6">
              <Factory size={12} /> A venture of Ellora Group
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
              Ready-Mix Concrete,<br />
              <span className="text-white">Delivered Right.</span>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
              ELR Industries supplies IS-code certified ready-mix concrete from a fully automated
              batching plant, with in-house lab testing on every grade we deliver.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-slate-100 text-[#1F7A6E] font-bold rounded-sm transition-colors text-sm"
                data-testid="hero-quote-cta">
                Request a Quote <ChevronRight size={16} />
              </a>
              <a href="#about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white hover:bg-white/10 rounded-sm transition-colors text-sm font-medium">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── About strip ── */}
      <section id="about" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { num: 'M15–M40', label: 'Concrete Grades Supplied' },
              { num: 'IS 4926', label: 'Batching Standard' },
              { num: 'Automated', label: 'Batching Plant' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#1F7A6E]">{s.num}</div>
                <div className="text-sm text-slate-500 mt-1 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[#1F7A6E] font-bold text-xs uppercase tracking-widest mb-3">What We Supply</div>
            <h2 className="text-3xl font-extrabold text-slate-900">Our Products & Services</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title}
                className="bg-slate-50 border border-slate-200 rounded-sm p-6 hover:border-[#1F7A6E]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-sm bg-[#1F7A6E]/10 flex items-center justify-center mb-4">
                  <s.icon size={20} className="text-[#1F7A6E]" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-2">{s.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quality ── */}
      <section id="quality" className="py-20 bg-[#1F7A6E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-white/70 font-bold text-xs uppercase tracking-widest mb-3">Built On Testing, Not Trust</div>
            <h2 className="text-3xl font-extrabold text-white">Quality You Can Verify</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto text-sm">
              Every batch that leaves our plant is traceable to a tested mix design —
              not just a recipe on paper.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {QUALITY_POINTS.map((p) => (
              <div key={p.title} className="bg-white/10 rounded-sm p-6">
                <FlaskConical size={22} className="text-white/60 mb-3" />
                <h3 className="text-white font-bold text-sm mb-2">{p.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[#1F7A6E] font-bold text-xs uppercase tracking-widest mb-3">Get In Touch</div>
            <h2 className="text-3xl font-extrabold text-slate-900">Request a Quote</h2>
            <p className="mt-3 text-slate-500 max-w-xl mx-auto text-sm">
              Tell us your grade, quantity, and site location — we'll get back with a delivery schedule and rate.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { icon: MapPin, label: 'Plant Address', value: '[Add ELR Industries plant address]' },
              { icon: Phone, label: 'Phone', value: '[Add contact number]' },
              { icon: Mail, label: 'Email', value: '[Add contact email]' },
            ].map((c) => (
              <div key={c.label} className="text-center">
                <div className="w-10 h-10 rounded-full bg-[#1F7A6E]/10 flex items-center justify-center mx-auto mb-3">
                  <c.icon size={18} className="text-[#1F7A6E]" />
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{c.label}</div>
                <div className="text-sm text-slate-700">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 text-white/60 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm">© {new Date().getFullYear()} ELR Industries Pvt. Ltd. All rights reserved.</div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
            <Link to="/portal/login" className="hover:text-white transition-colors">Customer Login</Link>
            <Link to="/login" className="hover:text-white transition-colors">Staff Portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
